import { useEffect, useState, useMemo } from "react"
import Container from "../../components/ui/Container.js"
import { CrearJuego, GestionarJuegos, JuegosHabilitados } from "./Juegos.js"
import { CrearUsuario, GestionarUsuarios } from "./Usuarios"
import { kebabify } from "../../helpers/utils.js"
import { AgregarWords, GestionarWords } from "./Words.js"
import { GestionarTags, AgregarTag } from "./Tags.js"
import { Historial } from "./Historial.js"
import { GestionarSugerencias } from "./Sugerencias.js"

import "./Admin.scss"
import { useBarrier } from "../../hooks/useSheriff.js"
import { AgregarConsigna, GestionarConsignas } from "./Consignas.js"
import { api } from "../../services/api/index.js"
import useFetch from "../../hooks/useFetch.js"
import { config } from "../../config.js"
import { useTranslation } from "react-i18next"
import { AgregarPyR, GestionarPyR } from "./Preguntas.js"
import { meets } from "../../helpers/jwt.js"

const SubMenu = ({ menu, setMenu, entries, title }) => {
  const handleClick = (game, e) => {
    e.preventDefault()
    const key = kebabify(game)
    setMenu(key)
    window.location.hash = `#${key}`
  }

  return (
    <>
      <h5 className="mb-2">
        <center>{title}</center>
      </h5>

      <div className="list-group mb-4">
        {entries.map((entry, ix) => {
          const classes = 
            "list-group-item  list-group-item-action" +
            (menu === kebabify(entry) ? " active" : "") +
            " textoblanco"
          return (
            <a
              key={ix}
              href="/#"
              onClick={handleClick.bind(this, entry)}
              className={classes}
            >
              {entry}
            </a>
          )
        })}
      </div>
    </>
  )
}

const SubMenuConsignas = ({ menu, setMenu }) => {
  const { t } = useTranslation(),
    entries = [
      t(`admin.menu.exercises.create`),
      t(`admin.menu.exercises.manage`),
    ]

  return (
    <SubMenu
      menu={menu}
      setMenu={setMenu}
      entries={entries}
      title={t(`admin.menu.exercises.title`)}
    />
  )
}

const SubMenuPreguntas = ({ menu, setMenu }) => {
  const { t } = useTranslation(),
    entries = [t(`admin.menu.qa.create`), t(`admin.menu.qa.manage`)]

  return (
    <SubMenu
      menu={menu}
      setMenu={setMenu}
      entries={entries}
      title={t(`admin.menu.qa.title`)}
    />
  )
}

const SubMenuJuegos = ({ menu, setMenu }) => {
  const { t } = useTranslation(),
    entries = [
      t(`admin.menu.games.create`),
      t(`admin.menu.games.manage`),
      t(`admin.menu.games.enabled`),
    ]

  return (
    <SubMenu
      menu={menu}
      setMenu={setMenu}
      entries={entries}
      title={t(`admin.menu.games.title`)}
    />
  )
}

const SubMenuUsuarios = ({ menu, setMenu }) => {
  const { t } = useTranslation(),
    entries = [t(`admin.menu.users.create`), t(`admin.menu.users.manage`)]

  return (
    <SubMenu
      menu={menu}
      setMenu={setMenu}
      entries={entries}
      title={t(`admin.menu.users.title`)}
    />
  )
}

const SubMenuWords = ({ menu, setMenu }) => {
  const { t } = useTranslation(),
    entries = [t(`admin.menu.words.add`), t(`admin.menu.words.manage`)]

  return (
    <SubMenu
      menu={menu}
      setMenu={setMenu}
      entries={entries}
      title={t(`admin.menu.words.title`)}
    />
  )
}

const SubMenuTags = ({ menu, setMenu }) => {
  const { t } = useTranslation(),
    entries = [t(`admin.menu.tags.add`), t(`admin.menu.tags.manage`)]

  return (
    <SubMenu
      menu={menu}
      setMenu={setMenu}
      entries={entries}
      title={t(`admin.menu.tags.title`)}
    />
  )
}

const SubMenuSistema = ({ menu, setMenu }) => {
  const { t } = useTranslation(),
    entries = [t(`admin.menu.system.log`), t(`admin.menu.system.suggestions`)]

  return (
    <SubMenu
      menu={menu}
      setMenu={setMenu}
      entries={entries}
      title={t(`admin.menu.system.title`)}
    />
  )
}

const Bienvenida = () => {
  const { url, opt } = useMemo(() => api.system.status(), []),
    [data, isPending] = useFetch({ url, opt })

  const { t } = useTranslation()

  return (
    <>
      <center className="py-5">
        <h2>{t(`admin.header`)}</h2>
        {!isPending && (
          <>
            <h6>srv build {data.build}</h6>
            <h6>cli build {config.cli_build}</h6>
          </>
        )}
      </center>
    </>
  )
}

const Admin = () => {
  const [menu, setMenu] = useState(null)

  // seteo el hash de la url
  useEffect(() => {
    const h = window.location.hash
    if (h.length) setMenu(decodeURI(h.slice(1)))
  }, [])

  useBarrier(["admin", "teacher"])

  const { t } = useTranslation(),
    on = (x) => menu === kebabify(t(x))

  return (
    <Container>
      <div className="container">
        <div className="row">
          <div className="col-lg-3 col-12">
            <SubMenuConsignas menu={menu} setMenu={setMenu} />

            <SubMenuPreguntas menu={menu} setMenu={setMenu} />

            <SubMenuJuegos menu={menu} setMenu={setMenu} />

            {meets(["admin"]) && (
              <SubMenuUsuarios menu={menu} setMenu={setMenu} />
            )}

            {meets(["admin"]) && <SubMenuWords menu={menu} setMenu={setMenu} />}

            {meets(["admin"]) && <SubMenuTags menu={menu} setMenu={setMenu} />}

            {meets(["admin"]) && (
              <SubMenuSistema menu={menu} setMenu={setMenu} />
            )}
          </div>
          <div className="col-lg-9 col-12">
            {!menu && <Bienvenida />}

            {/* consignas */}
            {on(`admin.menu.exercises.create`) && (
              <AgregarConsigna setMenu={setMenu} />
            )}
            {on(`admin.menu.exercises.manage`) && <GestionarConsignas />}

            {/* p&r */}
            {on(`admin.menu.qa.create`) && <AgregarPyR setMenu={setMenu} />}
            {on(`admin.menu.qa.manage`) && <GestionarPyR />}

            {/* juegos */}
            {on(`admin.menu.games.create`) && <CrearJuego setMenu={setMenu} />}
            {on(`admin.menu.games.manage`) && <GestionarJuegos />}
            {on(`admin.menu.games.enabled`) && <JuegosHabilitados />}

            {/* usuarios */}
            {on(`admin.menu.users.create`) && <CrearUsuario />}
            {on(`admin.menu.users.manage`) && <GestionarUsuarios />}

            {/* words */}
            {on(`admin.menu.words.add`) && <AgregarWords />}
            {on(`admin.menu.words.manage`) && <GestionarWords />}

            {/* tags */}
            {on(`admin.menu.tags.add`) && <AgregarTag />}
            {on(`admin.menu.tags.manage`) && <GestionarTags />}

            {/* sistema */}
            {on(`admin.menu.system.log`) && <Historial />}
            {on(`admin.menu.system.suggestions`) && <GestionarSugerencias />}
          </div>
        </div>
      </div>
    </Container>
  )
}

export default Admin
